import { Controller } from "@hotwired/stimulus"

/**
 * Flash Controller
 *
 * Handles automatic dismissal and cleanup of flash messages.
 *
 * Features:
 * - Auto-hide flash messages after a configurable delay (default: 5 seconds)
 * - Clear flash messages on Turbo navigation
 * - Prevent multiple flash messages from stacking
 * - Handle both Bootstrap alerts and legacy .notice elements
 *
 * Usage:
 *   <div data-controller="flash">
 *     <div class="alert" data-flash-target="message" data-flash-auto-hide-value="true">
 *       Your message here
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["message"]
  static values = {
    autoHide: { type: Boolean, default: true },
    delay: { type: Number, default: 5000 } // 5 seconds
  }

  /**
   * Initialize the controller and set up event listeners
   */
  connect() {
    console.log("Flash controller connected")

    // Auto-hide flash messages if enabled
    if (this.autoHideValue && this.hasMessageTarget) {
      this.scheduleAutoHide()
    }

    // Set up Turbo navigation listeners
    this.boundBeforeVisit = this.handleBeforeVisit.bind(this)
    this.boundBeforeRender = this.handleBeforeRender.bind(this)

    document.addEventListener("turbo:before-visit", this.boundBeforeVisit)
    document.addEventListener("turbo:before-render", this.boundBeforeRender)
  }

  /**
   * Clean up event listeners when controller disconnects
   */
  disconnect() {
    console.log("Flash controller disconnected")

    if (this.autoHideTimer) {
      clearTimeout(this.autoHideTimer)
    }

    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit)
    document.removeEventListener("turbo:before-render", this.boundBeforeRender)
  }

  /**
   * Schedule automatic hiding of flash messages
   */
  scheduleAutoHide() {
    // Clear any existing timer
    if (this.autoHideTimer) {
      clearTimeout(this.autoHideTimer)
    }

    // Schedule auto-hide
    this.autoHideTimer = setTimeout(() => {
      this.hideAllMessages()
    }, this.delayValue)
  }

  /**
   * Hide all flash messages with animation
   */
  hideAllMessages() {
    this.messageTargets.forEach(message => {
      this.hideMessage(message)
    })
  }

  /**
   * Hide a single message with fade animation
   * @param {HTMLElement} message - The message element to hide
   */
  hideMessage(message) {
    // Use Bootstrap's fade animation if available
    if (message.classList.contains('alert')) {
      message.classList.remove('show')
      message.classList.add('fade')

      // Remove from DOM after animation completes
      setTimeout(() => {
        message.remove()
      }, 150) // Bootstrap fade duration
    } else {
      // Fallback for non-Bootstrap elements (legacy .notice)
      message.style.transition = 'opacity 0.5s ease-out'
      message.style.opacity = '0'

      setTimeout(() => {
        message.remove()
      }, 500)
    }
  }

  /**
   * Handle Turbo before-visit event
   * Clear flash messages before navigating to prevent persistence
   */
  handleBeforeVisit() {
    console.log("Flash: Clearing messages before navigation")
    this.clearAllMessages()
  }

  /**
   * Handle Turbo before-render event
   *
   * NOTE: We intentionally do NOT clear messages here. Clearing before-render
   * was preventing flash messages from appearing after redirects (e.g., after
   * deleting an admin). The new page's flash content will naturally replace
   * any existing flash content through Turbo's morphing.
   *
   * If duplicate messages become an issue, we should handle it differently
   * (e.g., using data-turbo-temporary attribute which Turbo already respects).
   */
  handleBeforeRender() {
    // Intentionally empty - let Turbo's morph handle flash content naturally
    console.log("Flash: Before render (not clearing - new content will replace)")
  }

  /**
   * Immediately clear all flash messages without animation
   */
  clearAllMessages() {
    this.messageTargets.forEach(message => {
      message.remove()
    })
  }

  /**
   * Manual dismiss action (triggered by dismiss button clicks)
   */
  dismiss(event) {
    const message = event.currentTarget.closest('[data-flash-target="message"]')
    if (message) {
      this.hideMessage(message)
    }
  }
}
